/**
 * DC Cooling Network Scenario Runner
 *
 * Executes transient scenarios on the cooling network NS solver.
 * Each scenario:
 *   1. Initialises from a converged steady-state solution
 *   2. Injects time-dependent events (pump trips, chiller loss, load ramps)
 *   3. Steps the coupled N-S solver with HX thermal coupling
 *   4. Logs all outputs at every timestep
 *   5. Flags threshold exceedances and convergence failures
 *
 * Scenarios are defined as config objects — nothing is hardcoded.
 */

import type { NSSolverState } from '../types';
import type { SimEvent } from '../../types';
import { networkSimpleStep, networkMaxTimeStep } from '../network/nsNetwork';
import {
  createCoolingNetworkNS,
  updateHXCoupling,
  CoolingNetworkConfig,
  DEFAULT_COOLING_CONFIG,
} from './coolingNetwork';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ScenarioEvent {
  time: number;           // Time to trigger [s]
  type: 'pump_trip' | 'chiller_loss' | 'load_change' | 'pump_start';
  target: string;         // Component ID (e.g., 'pri_pump_0', 'heat_rejection', 'rack_cdu_3')
  params: Record<string, number>;  // Event-specific parameters
}

export interface ScenarioConfig {
  name: string;
  duration: number;            // Total simulation time [s]
  dt: number;                  // Base timestep [s]
  steadyStateMaxIter: number;  // Max SIMPLE iterations for steady-state convergence
  steadyStateTol: number;      // Convergence tolerance for steady-state
  events: ScenarioEvent[];
  networkConfig?: CoolingNetworkConfig;

  // Threshold monitoring
  rackTempWarning_C: number;   // Warning threshold for rack branch inlet [°C]
  rackTempCritical_C: number;  // Critical threshold [°C]
}

export interface TimestepLog {
  time: number;
  converged: boolean;
  residuals: { mass: number; momentum: number; energy: number };
  iterations: number;

  // Flow rates [kg/s]
  primaryPumpFlows: number[];
  secondaryPumpFlows: number[];
  hxFlows_primary: number[];
  hxFlows_secondary: number[];
  rackBranchFlows: number[];

  // Pressures [Pa]
  primaryPumpDischarge: number[];
  secondaryPumpDischarge: number[];
  rackBranchInletP: number[];

  // Temperatures [°C]
  primaryPumpDischargeT: number[];
  primaryHxOutletT: number[];      // Primary side outlet (warmed)
  secondarySupplyT: number;        // Secondary supply header
  rackBranchInletT: number[];
  rackBranchOutletT: number[];
  heatRejectionOutletT: number;

  // Energy [W]
  totalHeatRejection: number;
  totalITLoad: number;
  hxTransferRates: number[];

  // Alerts
  alerts: string[];
}

export interface ScenarioResult {
  config: ScenarioConfig;
  steadyStateConverged: boolean;
  steadyStateIterations: number;
  log: TimestepLog[];
  summary: {
    maxRackInletTemp_C: number;
    timeToWarning_s: number | null;
    timeToCritical_s: number | null;
    totalUnconvergedSteps: number;
  };
}

// ─── Predefined Scenarios ────────────────────────────────────────────────────

export const SCENARIOS: Record<string, ScenarioConfig> = {
  steady_state: {
    name: 'Steady-State — Design Conditions (100% IT Load)',
    duration: 0,
    dt: 0.1,
    steadyStateMaxIter: 500,
    steadyStateTol: 1e-4,
    events: [],
    rackTempWarning_C: 40,
    rackTempCritical_C: 45,
  },

  primary_pump_trip: {
    name: 'Primary Pump Trip (Coast-Down)',
    duration: 120,
    dt: 0.1,
    steadyStateMaxIter: 300,
    steadyStateTol: 1e-4,
    events: [
      { time: 10, type: 'pump_trip', target: 'pri_pump_0', params: {} },
    ],
    rackTempWarning_C: 40,
    rackTempCritical_C: 45,
  },

  // R3-C3 flagship: FULL heat-rejection-loop loss — every duty primary pump
  // trips together, so primary flow coasts down on rotational inertia (~15-20 s)
  // and the heat-rejection capacity fades with the flow. This is the ride-through
  // scenario the resilience analysis (src/lib/sim/scenarios) runs. Tripping both
  // duty pumps (a shared bus/power-block loss) is the honest worst case for the
  // heat-rejection side; the standby pump is NOT auto-started here (that is the
  // failure being studied). On trimmed configs the pri_pump_1 event no-ops.
  primary_pump_trip_full: {
    name: 'Primary Pump Trip — Full Heat-Rejection Loss (Coast-Down)',
    duration: 400,
    dt: 0.1,
    steadyStateMaxIter: 300,
    steadyStateTol: 1e-4,
    events: [
      { time: 10, type: 'pump_trip', target: 'pri_pump_0', params: {} },
      { time: 10, type: 'pump_trip', target: 'pri_pump_1', params: {} },
    ],
    rackTempWarning_C: 40,
    rackTempCritical_C: 45,
  },

  chiller_loss: {
    name: 'Chiller / Heat Rejection Loss',
    duration: 600,
    dt: 0.5,
    steadyStateMaxIter: 300,
    steadyStateTol: 1e-4,
    events: [
      // Step heat rejection to 2/3 capacity (one unit lost)
      { time: 10, type: 'chiller_loss', target: 'heat_rejection', params: { fraction: 0.667 } },
    ],
    rackTempWarning_C: 40,
    rackTempCritical_C: 45,
  },

  combined: {
    name: 'Combined — Pump Trip + Chiller Loss',
    duration: 300,
    dt: 0.2,
    steadyStateMaxIter: 300,
    steadyStateTol: 1e-4,
    events: [
      { time: 10, type: 'pump_trip', target: 'pri_pump_0', params: {} },
      { time: 60, type: 'chiller_loss', target: 'heat_rejection', params: { fraction: 0.667 } },
    ],
    rackTempWarning_C: 40,
    rackTempCritical_C: 45,
  },

  it_load_ramp: {
    name: 'IT Load Ramp (50% → 100%)',
    duration: 120,
    dt: 0.2,
    steadyStateMaxIter: 300,
    steadyStateTol: 1e-4,
    events: [
      // Load ramp handled specially — ramps from 50% to 100% over 30s starting at t=10
      { time: 10, type: 'load_change', target: 'all_racks', params: { startFraction: 0.5, endFraction: 1.0, rampDuration: 30 } },
    ],
    rackTempWarning_C: 40,
    rackTempCritical_C: 45,
  },
};

// ─── Scenario Execution ──────────────────────────────────────────────────────

/** Liquid-water specific heat used for the energy-residual scale [J/(kg·K)]. */
const CP_WATER_J_KGK = 4186;

/**
 * Problem-scale reference values for residual NORMALIZATION (C-F5).
 *
 * The solver's raw residuals are SI magnitudes — mass/momentum in kg/s,
 * energy in J/kg per outer iteration. Checking those against ABSOLUTE
 * tolerances (1e-4 / 1e-3 / 1e-1) can never converge on a utility-scale
 * network: 1e-4 kg/s on a ~1430 kg/s design loop demands 7e-8 RELATIVE
 * mass closure, so the residuals plateaued (1e3–1.9e4) and
 * `steadyStateConverged` was permanently false. Normalizing by the design
 * scale makes the tolerances mean what they say:
 *   ṁ_ref = largest duty-pump group design flow  [kg/s]
 *   h_ref = cp·ΔT_design (design loop temperature rise) [J/kg]
 */
export function steadyStateReferenceScales(
  config: CoolingNetworkConfig,
): { mdotRef_kgs: number; hRef_Jkg: number } {
  const mdotRef_kgs = Math.max(
    1,
    config.numPrimaryPumpsDuty * config.primaryPumpFlow_kgs,
    config.numSecondaryPumpsDuty * config.secondaryPumpFlow_kgs,
  );
  const hRef_Jkg = Math.max(
    1e3,
    CP_WATER_J_KGK * Math.abs(config.primaryReturnTemp_C - config.primarySupplyTemp_C),
    CP_WATER_J_KGK * Math.abs(config.secondaryReturnTemp_C - config.secondarySupplyTemp_C),
  );
  return { mdotRef_kgs, hRef_Jkg };
}

/**
 * Run steady-state initialisation to convergence.
 * Iterates SIMPLE steps with large dt until NORMALIZED residuals are below
 * tolerance (C-F5 — see steadyStateReferenceScales).
 *
 * Convergence criterion (all relative to problem scale):
 *   mass/ṁ_ref     < tol        (0.01% of design flow at tol = 1e-4)
 *   momentum/ṁ_ref < 10·tol     (junction imbalance, 0.1% of design flow)
 *   energy/h_ref   < 10·tol     (per-iteration Δh under 0.1% of the design
 *                                loop temperature rise ≈ 0.01 K — a genuine
 *                                "thermally stationary" indicator. The old
 *                                ladder's ×1000 would, as a RELATIVE bound,
 *                                accept 10% of ΔT_design ≈ 1 K PER ITERATION,
 *                                which is not steady state.)
 *
 * NOTE: `state.converged` (the solver's own per-step flag) is deliberately
 * NOT required here — it uses the same absolute tolerances, tuned for small
 * canvas networks, and never fires at utility scale. The normalized check IS
 * the honest steady-state criterion for the scenario runner.
 */
function runSteadyState(
  state: NSSolverState,
  config: CoolingNetworkConfig,
  maxIter: number,
  tolerance: number,
): { converged: boolean; iterations: number } {
  // Pseudo-timestep, CFL-CAPPED (C-F5 stability fix, probe-verified).
  //
  // The previous fixed dt_ss = 1.0 s is ~100–200× the stable step of the
  // EXPLICIT momentum predictor for these pipes (dx ≈ 1.5–2.5 m, c ≈ 1500
  // m/s ⇒ CFL·5 step ≈ 5–8 ms): the predictor's friction/pressure terms
  // overshot every iteration and the loop "steady state" was numerical
  // noise pinned against the ±50 m/s velocity clamp (probe: 3 768 kg/s at
  // dt = 1.0 vs 359.9 kg/s ≈ rated at the CFL-capped dt on the identical
  // network). Same recipe as the transient loop below and the live
  // stepAdapter: dt = min(cap, max(floor, networkMaxTimeStep(state, 5))),
  // re-evaluated every iteration as the flow field evolves.
  const { mdotRef_kgs, hRef_Jkg } = steadyStateReferenceScales(config);

  for (let i = 0; i < maxIter; i++) {
    const dt_ss = Math.min(1.0, Math.max(0.005, networkMaxTimeStep(state, 5)));
    updateHXCoupling(state, config);
    networkSimpleStep(state, dt_ss);

    const massRel = state.residuals.mass / mdotRef_kgs;
    const momentumRel = state.residuals.momentum / mdotRef_kgs;
    const energyRel = state.residuals.energy / hRef_Jkg;

    if (isFinite(massRel) && isFinite(momentumRel) && isFinite(energyRel) &&
        massRel < tolerance &&
        momentumRel < tolerance * 10 &&
        energyRel < tolerance * 10) {
      return { converged: true, iterations: i + 1 };
    }
  }

  return { converged: false, iterations: maxIter };
}

/**
 * Apply scenario events at the current time.
 */
function applyEvents(
  state: NSSolverState,
  config: CoolingNetworkConfig,
  events: ScenarioEvent[],
  time: number,
  dt: number,
): void {
  for (const event of events) {
    if (time < event.time || time >= event.time + dt) continue; // Not yet triggered or already past trigger point

    switch (event.type) {
      case 'pump_trip': {
        // Set pump speed to 0 — inertia equation handles coast-down
        const pump = state.components.get(event.target);
        if (pump) {
          pump.params.speed = 0;
        }
        break;
      }

      case 'pump_start': {
        const pump = state.components.get(event.target);
        if (pump) {
          pump.params.speed = event.params.speed ?? 1.0;
        }
        break;
      }

      case 'chiller_loss': {
        const hr = state.components.get(event.target);
        if (hr) {
          // Reduce capacity by fraction
          const designQ = config.heatRejectionCapacity_W;
          hr.params.Q_rate = -designQ * (event.params.fraction ?? 0);
        }
        break;
      }

      case 'load_change': {
        // Handled per-timestep in the main loop (ramp logic)
        break;
      }
    }
  }
}

/**
 * Apply continuous load ramp events.
 */
function applyLoadRamps(
  state: NSSolverState,
  config: CoolingNetworkConfig,
  events: ScenarioEvent[],
  time: number,
): void {
  for (const event of events) {
    if (event.type !== 'load_change') continue;

    const { startFraction, endFraction, rampDuration } = event.params;
    const rampStart = event.time;
    const rampEnd = rampStart + rampDuration;

    let loadFraction: number;
    if (time < rampStart) {
      loadFraction = startFraction;
    } else if (time >= rampEnd) {
      loadFraction = endFraction;
    } else {
      // Linear ramp
      const progress = (time - rampStart) / rampDuration;
      loadFraction = startFraction + (endFraction - startFraction) * progress;
    }

    // Apply to all rack CDUs — heat rejection is NOT adjusted here.
    // The mismatch between IT load and fixed heat rejection capacity
    // drives the thermal transient that must propagate through the network.
    for (let i = 0; i < config.numRackBranches; i++) {
      const cdu = state.components.get(`rack_cdu_${i}`);
      if (cdu) {
        cdu.params.Q_rate = config.rackHeatLoad_W * loadFraction;
      }
    }
  }
}

/**
 * Extract output log from the current solver state.
 */
function extractLog(
  state: NSSolverState,
  config: CoolingNetworkConfig,
  time: number,
  rackTempWarning_C: number = 40,
  rackTempCritical_C: number = 45,
): TimestepLog {
  const alerts: string[] = [];

  // Helper: get pipe face mdot
  const pipeMdot = (pipeId: string, faceIdx: number): number => {
    const pipe = state.pipes.get(pipeId);
    return pipe ? pipe.faces[faceIdx].mdot : 0;
  };

  // Helper: get pipe cell temperature [°C]
  const pipeTemp = (pipeId: string, cellIdx: number): number => {
    const pipe = state.pipes.get(pipeId);
    return pipe ? pipe.cells[Math.min(cellIdx, pipe.nCells - 1)].T - 273.15 : 0;
  };

  // Helper: get pipe cell pressure [Pa]
  const pipeP = (pipeId: string, cellIdx: number): number => {
    const pipe = state.pipes.get(pipeId);
    return pipe ? pipe.cells[Math.min(cellIdx, pipe.nCells - 1)].P : 0;
  };

  const nc = config.pipeNcells;

  // Pump flows
  const primaryPumpFlows = Array.from({ length: config.numPrimaryPumps }, (_, i) =>
    pipeMdot(`p_pri_pump_out_${i}`, 0));
  const secondaryPumpFlows = Array.from({ length: config.numSecondaryPumps }, (_, i) =>
    pipeMdot(`p_sec_pump_out_${i}`, 0));

  // HX flows
  const hxFlows_primary = Array.from({ length: config.numPlateHX }, (_, i) =>
    pipeMdot(`p_pri_hx_in_${i}`, 0));
  const hxFlows_secondary = Array.from({ length: config.numPlateHX }, (_, i) =>
    pipeMdot(`p_sec_hx_in_${i}`, 0));

  // Rack branch flows
  const rackBranchFlows = Array.from({ length: config.numRackBranches }, (_, i) =>
    pipeMdot(`p_rack_supply_${i}`, 0));

  // Pressures
  const primaryPumpDischarge = Array.from({ length: config.numPrimaryPumps }, (_, i) =>
    pipeP(`p_pri_pump_out_${i}`, 0));
  const secondaryPumpDischarge = Array.from({ length: config.numSecondaryPumps }, (_, i) =>
    pipeP(`p_sec_pump_out_${i}`, 0));
  const rackBranchInletP = Array.from({ length: config.numRackBranches }, (_, i) =>
    pipeP(`p_rack_supply_${i}`, 0));

  // Temperatures
  const primaryPumpDischargeT = Array.from({ length: config.numPrimaryPumps }, (_, i) =>
    pipeTemp(`p_pri_pump_out_${i}`, 0));
  const primaryHxOutletT = Array.from({ length: config.numPlateHX }, (_, i) =>
    pipeTemp(`p_pri_hx_out_${i}`, nc - 1));
  const secondarySupplyT = pipeTemp('p_sec_supply_header', 0);
  const rackBranchInletT = Array.from({ length: config.numRackBranches }, (_, i) =>
    pipeTemp(`p_rack_supply_${i}`, 0));
  const rackBranchOutletT = Array.from({ length: config.numRackBranches }, (_, i) =>
    pipeTemp(`p_rack_return_${i}`, nc - 1));
  const heatRejectionOutletT = pipeTemp('p_pri_hr_to_tank', nc - 1);

  // Energy
  const hxTransferRates = Array.from({ length: config.numPlateHX }, (_, i) =>
    state.components.get(`pri_hx_${i}`)?.params.Q_rate ?? 0);
  const totalHeatRejection = Math.abs(state.components.get('heat_rejection')?.params.Q_rate ?? 0);
  const totalITLoad = Array.from({ length: config.numRackBranches }, (_, i) =>
    state.components.get(`rack_cdu_${i}`)?.params.Q_rate ?? 0).reduce((a, b) => a + b, 0);

  // Threshold checks
  for (let i = 0; i < config.numRackBranches; i++) {
    const T = rackBranchInletT[i];
    if (T >= rackTempCritical_C) {
      alerts.push(`CRITICAL: Rack ${i} inlet temp ${T.toFixed(1)}°C exceeds ${rackTempCritical_C}°C`);
    } else if (T >= rackTempWarning_C) {
      alerts.push(`WARNING: Rack ${i} inlet temp ${T.toFixed(1)}°C exceeds ${rackTempWarning_C}°C`);
    }
  }

  if (!state.converged) {
    alerts.push(`UNCONVERGED: mass=${state.residuals.mass.toExponential(2)}, ` +
      `momentum=${state.residuals.momentum.toExponential(2)}, ` +
      `energy=${state.residuals.energy.toExponential(2)}`);
  }

  return {
    time,
    converged: state.converged,
    residuals: { ...state.residuals },
    iterations: state.iteration,
    primaryPumpFlows,
    secondaryPumpFlows,
    hxFlows_primary,
    hxFlows_secondary,
    rackBranchFlows,
    primaryPumpDischarge,
    secondaryPumpDischarge,
    rackBranchInletP,
    primaryPumpDischargeT,
    primaryHxOutletT,
    secondarySupplyT,
    rackBranchInletT,
    rackBranchOutletT,
    heatRejectionOutletT,
    totalHeatRejection,
    totalITLoad,
    hxTransferRates,
    alerts,
  };
}

// ─── Main Runner ─────────────────────────────────────────────────────────────

/**
 * Execute a cooling network scenario from config.
 *
 * Returns the full timestep log and summary statistics.
 * This is synchronous — for the browser, wrap in a Web Worker or run in chunks.
 */
export function runScenario(scenario: ScenarioConfig): ScenarioResult {
  const netConfig = scenario.networkConfig ?? DEFAULT_COOLING_CONFIG;

  // If IT load ramp starts at 50%, initialise CDUs at that level
  const loadInitEvent = scenario.events.find(e => e.type === 'load_change');
  const initialLoadFraction = loadInitEvent?.params.startFraction ?? 1.0;

  // Build network
  const state = createCoolingNetworkNS(netConfig);

  // Set initial load
  for (let i = 0; i < netConfig.numRackBranches; i++) {
    const cdu = state.components.get(`rack_cdu_${i}`);
    if (cdu) cdu.params.Q_rate = netConfig.rackHeatLoad_W * initialLoadFraction;
  }
  const hr = state.components.get('heat_rejection');
  if (hr) hr.params.Q_rate = -netConfig.rackHeatLoad_W * netConfig.numRackBranches * initialLoadFraction;

  // ─── Steady-state initialisation ───
  const ss = runSteadyState(state, netConfig, scenario.steadyStateMaxIter, scenario.steadyStateTol);

  const log: TimestepLog[] = [];
  log.push(extractLog(state, netConfig, 0, scenario.rackTempWarning_C, scenario.rackTempCritical_C));

  if (scenario.duration <= 0) {
    // Steady-state only
    return {
      config: scenario,
      steadyStateConverged: ss.converged,
      steadyStateIterations: ss.iterations,
      log,
      summary: buildSummary(log, scenario),
    };
  }

  // ─── Transient simulation ───
  // Reset time for transient phase
  state.time = 0;

  let time = 0;
  const dt_base = scenario.dt;

  while (time < scenario.duration) {
    // Adaptive timestep: respect CFL but don't go below dt_base/10
    const dt_max = Math.max(dt_base / 10, networkMaxTimeStep(state, 3));
    const dt = Math.min(dt_base, dt_max);

    // Apply discrete events
    applyEvents(state, netConfig, scenario.events, time, dt);

    // Apply continuous ramps
    applyLoadRamps(state, netConfig, scenario.events, time);

    // Update thermal coupling between loops
    updateHXCoupling(state, netConfig);

    // Step solver
    networkSimpleStep(state, dt);

    time += dt;

    // Log at base timestep intervals (avoid excessive logging for small sub-steps)
    if (log.length === 0 || time - log[log.length - 1].time >= dt_base * 0.99) {
      log.push(extractLog(state, netConfig, time, scenario.rackTempWarning_C, scenario.rackTempCritical_C));
    }
  }

  return {
    config: scenario,
    steadyStateConverged: ss.converged,
    steadyStateIterations: ss.iterations,
    log,
    summary: buildSummary(log, scenario),
  };
}

/**
 * Run a predefined scenario by its key in {@link SCENARIOS}, with optional
 * config overrides (e.g. a trimmed `networkConfig` + short `duration` for tests,
 * or a different threshold). Thin, one-call entry point used by the resilience /
 * ride-through analysis (src/lib/sim/scenarios) and by UI presets.
 *
 * @throws if the key is not a known scenario.
 */
export function runScenarioByKey(
  key: string,
  overrides: Partial<ScenarioConfig> = {},
): ScenarioResult {
  const base = SCENARIOS[key];
  if (!base) {
    throw new Error(
      `Unknown cooling scenario key '${key}'. Known keys: ${Object.keys(SCENARIOS).join(', ')}`,
    );
  }
  return runScenario({ ...base, ...overrides });
}

function buildSummary(log: TimestepLog[], scenario: ScenarioConfig): ScenarioResult['summary'] {
  let maxRackInletTemp = -Infinity;
  let timeToWarning: number | null = null;
  let timeToCritical: number | null = null;
  let unconverged = 0;

  for (const entry of log) {
    if (!entry.converged) unconverged++;
    for (const T of entry.rackBranchInletT) {
      if (T > maxRackInletTemp) maxRackInletTemp = T;
      if (T >= scenario.rackTempWarning_C && timeToWarning === null) {
        timeToWarning = entry.time;
      }
      if (T >= scenario.rackTempCritical_C && timeToCritical === null) {
        timeToCritical = entry.time;
      }
    }
  }

  return {
    maxRackInletTemp_C: maxRackInletTemp,
    timeToWarning_s: timeToWarning,
    timeToCritical_s: timeToCritical,
    totalUnconvergedSteps: unconverged,
  };
}

// ─── R2-B4 — SimEvent preset bridge (productionization) ──────────────────────
//
// The scenario vocabulary above (ScenarioEvent + SCENARIOS) is now reachable
// from the live simulation UI as one-click, loadable SimEvent sets. This bridge
// maps each ScenarioEvent onto the concrete component param a SimEvent mutates,
// so the canned DC-cooling transients (pump-trip ride-through, chiller-loss,
// combined, IT-load ramp) drive the SAME live NS step adapter as hand-authored
// events. The full runScenario() summary path (time_to_warning / time_to_
// critical) stays reachable via runScenarioByKey() below.

/** Component-id targets a preset expands `all_racks` into, given a config. */
function rackTargets(config: CoolingNetworkConfig): string[] {
  return Array.from({ length: config.numRackBranches }, (_, i) => `rack_cdu_${i}`);
}

/**
 * Expand ONE ScenarioEvent into one-or-more productionized {@link SimEvent}s,
 * mapping the scenario type onto the concrete param a live component exposes:
 *   pump_trip    → speed = 0                     (dynamic-speed pump coast-down)
 *   pump_start   → speed = params.speed ?? 1.0
 *   chiller_loss → Q_rate = −capacity·fraction   (heat-rejection duty step [W])
 *   load_change  → Q_rate ramp per rack CDU      (rackHeatLoad·start → ·end [W])
 *
 * `idPrefix` keeps ids stable + unique across a preset's events.
 */
export function scenarioEventToSimEvents(
  ev: ScenarioEvent,
  config: CoolingNetworkConfig,
  idPrefix: string,
): SimEvent[] {
  switch (ev.type) {
    case 'pump_trip':
      return [{ id: idPrefix, t: ev.time, targetNodeId: ev.target, param: 'speed', value: 0,
        label: `Pump trip — ${ev.target}` }];

    case 'pump_start':
      return [{ id: idPrefix, t: ev.time, targetNodeId: ev.target, param: 'speed',
        value: ev.params.speed ?? 1.0, label: `Pump start — ${ev.target}` }];

    case 'chiller_loss':
      return [{ id: idPrefix, t: ev.time, targetNodeId: ev.target, param: 'Q_rate',
        value: -config.heatRejectionCapacity_W * (ev.params.fraction ?? 0),
        label: `Chiller loss — ${((ev.params.fraction ?? 0) * 100).toFixed(0)}% capacity` }];

    case 'load_change': {
      const startF = ev.params.startFraction ?? 1.0;
      const endF = ev.params.endFraction ?? 1.0;
      const duration = ev.params.rampDuration ?? 0;
      const targets = ev.target === 'all_racks' ? rackTargets(config) : [ev.target];
      return targets.map((targetNodeId, i) => ({
        id: `${idPrefix}-${i}`,
        t: ev.time,
        targetNodeId,
        param: 'Q_rate',
        ramp: {
          from: config.rackHeatLoad_W * startF,
          to: config.rackHeatLoad_W * endF,
          duration,
        },
        label: `IT load ${(startF * 100).toFixed(0)}→${(endF * 100).toFixed(0)}% — ${targetNodeId}`,
      }));
    }

    default:
      return [];
  }
}

/** Expand a whole {@link ScenarioConfig}'s events into a flat SimEvent set. */
export function scenarioToSimEvents(
  scenario: ScenarioConfig,
  config: CoolingNetworkConfig = scenario.networkConfig ?? DEFAULT_COOLING_CONFIG,
): SimEvent[] {
  return scenario.events.flatMap((ev, idx) =>
    scenarioEventToSimEvents(ev, config, `${scenario.name}#${idx}`),
  );
}

/** One loadable transient preset: display name + description + its SimEvent set. */
export interface SimEventPreset {
  key: string;
  name: string;
  description: string;
  events: SimEvent[];
}

/**
 * Named, one-click-loadable SimEvent presets derived from {@link SCENARIOS}
 * against {@link DEFAULT_COOLING_CONFIG}. Steady-state (no events) is omitted.
 * Targets are the DC-cooling-network component ids (`pri_pump_0`,
 * `heat_rejection`, `rack_cdu_*`); on a canvas lacking a target the step
 * adapter skips it silently. These feed R3's cooling-failure ride-through.
 */
export const SIM_EVENT_PRESETS: SimEventPreset[] = Object.entries(SCENARIOS)
  .filter(([, s]) => s.events.length > 0)
  .map(([key, s]) => ({
    key,
    name: s.name,
    description: `${s.events.length} event${s.events.length === 1 ? '' : 's'} · ${s.duration}s horizon`,
    events: scenarioToSimEvents(s),
  }));

/** Look up a preset's SimEvent set by key (empty array for an unknown key). */
export function buildEventPreset(key: string): SimEvent[] {
  return SIM_EVENT_PRESETS.find((p) => p.key === key)?.events ?? [];
}

// NOTE (R2-B4 × R3-C3 weld): B4 introduced a null-returning `runScenarioByKey(key)`
// to keep the time_to_warning/time_to_critical summary a named, reachable entry; C3
// then shipped the superset (config overrides + throw-on-unknown, defined above) that
// the resilience/ride-through analysis calls. The two collapsed to one — C3's version
// is canonical; it keeps the summary path first-class exactly as B4 intended.
